import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, X } from "lucide-react";
import {
	PieChart,
	Pie,
	Cell,
	Tooltip,
	ResponsiveContainer,
} from "recharts";
import { getCurrentBaselineRolloutSummary } from "../../lib/api";
import { PageLoader } from "../../components/ui/spinner";
import { PieValueTooltip } from "../../components/ui/chart-tooltip";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";

function toNumber(value, fallback = 0) {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : fallback;
}

const PIE_SLICES = [
	{ key: "updated", label: "Updated", color: "#22c55e" },
	{ key: "pending", label: "Pending", color: "#f59e0b" },
	{ key: "mandatory_wait", label: "Mandatory Wait", color: "#f97316" },
	{ key: "failed", label: "Failed", color: "#ef4444" },
];

function getSliceLabel(key) {
	return PIE_SLICES.find((slice) => slice.key === key)?.label ?? "All";
}

// `rolloutCategory = ""` rather than `const category = rolloutCategory || ""`
// (S7760). Equivalent here: category is only ever compared with === against
// string literals, so a null argument behaves exactly as "" did.
function getStatusMeta(device, rolloutCategory = "") {
	const category = rolloutCategory;
	const rawStatus = String(device?.status || "").toLowerCase();

	if (category === "mandatory_wait") {
		return { label: "mandatory wait", className: "bg-orange-100 text-orange-700" };
	}
	if (category === "updated") {
		return { label: "updated", className: "bg-green-100 text-green-700" };
	}
	if (category === "failed") {
		return { label: "failed", className: "bg-red-100 text-red-700" };
	}
	if (category === "pending") {
		return { label: "pending", className: "bg-amber-100 text-amber-700" };
	}

	if (rawStatus === "updated") {
		return { label: "updated", className: "bg-green-100 text-green-700" };
	}
	if (rawStatus === "failed") {
		return { label: "failed", className: "bg-red-100 text-red-700" };
	}

	return { label: rawStatus || "pending", className: "bg-amber-100 text-amber-700" };
}

export default function CurrentBaselineBreakdownPage() {
	const navigate = useNavigate();
	const [currentBaseline, setCurrentBaseline] = useState(null);
	const [loading, setLoading] = useState(true);
	const [selectedSlice, setSelectedSlice] = useState(null);
	const [filters, setFilters] = useState({ country: "", from_date: "", to_date: "" });

	useEffect(() => {
		let mounted = true;

		const fetchData = async () => {
			setLoading(true);
			try {
				const data = await getCurrentBaselineRolloutSummary(filters.country, filters.from_date, filters.to_date);
				if (!mounted) return;
				setCurrentBaseline(data ?? null);
			} catch (error) {
				if (!mounted) return;
				console.error("Failed to fetch current baseline breakdown", error);
				setCurrentBaseline(null);
			} finally {
				if (mounted) setLoading(false);
			}
		};

		fetchData();
		return () => {
			mounted = false;
		};
	}, [filters]);

	const pieData = useMemo(
		() =>
			PIE_SLICES.map((slice) => ({
				...slice,
				value: toNumber(currentBaseline?.[slice.key], 0),
			})).filter((slice) => slice.value > 0),
		[currentBaseline]
	);

	const countryOptions = useMemo(() => {
		const countrySet = new Set();
		const devicesByCategory = currentBaseline?.devices ?? {};

		Object.values(devicesByCategory).forEach((devices) => {
			(devices ?? []).forEach((device) => {
				const country = String(device?.current_country ?? "").trim();
				if (country) countrySet.add(country);
			});
		});

		return Array.from(countrySet).sort((a, b) => a.localeCompare(b));
	}, [currentBaseline]);

	const baselineDevices = currentBaseline?.devices ?? {};
	const tableDevices = selectedSlice
		? (baselineDevices[selectedSlice] ?? []).map((device) => ({
			...device,
			__rolloutCategory: selectedSlice,
		}))
		: Object.entries(baselineDevices).flatMap(([category, devices]) =>
			(devices ?? []).map((device) => ({
				...device,
				__rolloutCategory: category,
			}))
		);
	const totalDevices = pieData.reduce((sum, item) => sum + item.value, 0);

	const currentVersion = currentBaseline?.current_baseline || "--";

	const updatedCount = toNumber(currentBaseline?.updated, 0);
	const pendingCount = toNumber(currentBaseline?.pending, 0);
	const mandatoryWaitCount = toNumber(currentBaseline?.mandatory_wait, 0);
	const failedCount = toNumber(currentBaseline?.failed, 0);


	if (loading) return <PageLoader />;

	// active_rollout: false means no firmware is in the Rollout state - nothing
	// is being distributed to devices. Every chart and table below would render
	// as zeroes, which reads as a broken page rather than an idle fleet, so the
	// page says it plainly instead. A failed load leaves currentBaseline null
	// and this flag undefined, so the two never look alike.
	if (currentBaseline?.active_rollout === false) {
		return (
			<div className="space-y-5">
				<button
					onClick={() => navigate("/rollouts")}
					className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 border border-slate-200 rounded-lg px-3 py-2 bg-white transition-colors"
				>
					<ArrowLeft className="h-4 w-4" />
					Back to Rollout Summary
				</button>
				<div className="bg-white border border-slate-200 rounded-2xl px-6 py-14 text-center shadow-sm">
					<h1 className="text-xl font-bold text-slate-900">No active rollout</h1>
					<p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
						No firmware is currently rolling out, so there is no baseline to
						break down. Publish a firmware and create its rollout to see
						device progress here.
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-5">
			<div className="flex items-start justify-between gap-3">
				<div>
					<button
						onClick={() => navigate("/rollouts")}
						className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 border border-slate-200 rounded-lg px-3 py-2 bg-white transition-colors mb-2"
					>
						<ArrowLeft className="h-4 w-4" />
						Back to Rollout Summary
					</button>
					<h1 className="text-2xl font-bold text-slate-900">Current Baseline Breakdown</h1>
					<p className="text-sm text-slate-500 mt-1">Version {currentVersion}</p>
				</div>
				{selectedSlice && (
					<button
						onClick={() => setSelectedSlice(null)}
						className="flex items-center gap-1 text-xs px-3 py-1.5 border border-slate-300 rounded-md text-slate-600 hover:text-slate-800"
					>
						<X className="h-3 w-3" /> Clear filter
					</button>
				)}
			</div>

			<div className="bg-white border border-slate-200 rounded-2xl px-6 py-6 md:px-7 md:py-7 space-y-4 shadow-sm">
				<h2 className="text-base font-semibold text-slate-800">Filters</h2>
				<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
					<div>
						<label htmlFor="cbb-country-filter" className="block text-xs font-medium text-slate-600 mb-2">Country</label>
						<Select
							value={filters.country || "__all__"}
							onValueChange={(value) =>
								setFilters({
									...filters,
									country: value === "__all__" ? "" : value,
								})
							}
						>
							<SelectTrigger id="cbb-country-filter" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
								<SelectValue placeholder="All Countries" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="__all__">All Countries</SelectItem>
								{countryOptions.map((country) => (
									<SelectItem key={country} value={country}>
										{country}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div>
						<label htmlFor="cbb-from-date" className="block text-xs font-medium text-slate-600 mb-2">From Date</label>
						<input
							type="date"
							id="cbb-from-date"
							value={filters.from_date}
							onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
							className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
						/>
					</div>
					<div>
						<label htmlFor="cbb-to-date" className="block text-xs font-medium text-slate-600 mb-2">To Date</label>
						<input
							type="date"
							id="cbb-to-date"
							value={filters.to_date}
							onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
							className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
						/>
					</div>
				</div>
				{(filters.country || filters.from_date || filters.to_date) && (
					<button
						onClick={() => setFilters({ country: "", from_date: "", to_date: "" })}
						className="text-xs px-3 py-1.5 border border-slate-300 rounded-md text-slate-600 hover:text-slate-800 hover:border-slate-400 transition-colors"
					>
						Clear Filters
					</button>
				)}
			</div>

			<div className="bg-white border border-slate-200 rounded-2xl px-6 py-6 md:px-7 md:py-7 space-y-6 shadow-sm">
				<div>
					<h2 className="text-base font-semibold text-slate-800 mb-4">Device Breakdown</h2>
					{pieData.length === 0 ? (
						<p className="text-sm text-slate-400">No device data available.</p>
					) : (
						<div className="grid gap-6 lg:grid-cols-[360px,1fr] items-center">
							<div className="h-[300px] rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-4">
								<ResponsiveContainer width="100%" height="100%">
									<PieChart>
										<Pie
											data={pieData}
											cx="50%"
											cy="50%"
											innerRadius={72}
											outerRadius={108}
											paddingAngle={2}
											dataKey="value"
											onClick={(entry) => {
												setSelectedSlice((prev) => (prev === entry.key ? null : entry.key));
											}}
											cursor="pointer"
										>
											{pieData.map((entry) => (
												<Cell
													key={entry.key}
													fill={entry.color}
													opacity={selectedSlice && selectedSlice !== entry.key ? 0.35 : 1}
													stroke={selectedSlice === entry.key ? "#0f172a" : "#ffffff"}
													strokeWidth={selectedSlice === entry.key ? 3 : 2}
												/>
											))}
										</Pie>
										<Tooltip content={<PieValueTooltip />} />
										<text x="50%" y="48%" textAnchor="middle" dominantBaseline="middle" fill="#0f172a" fontSize="28" fontWeight="700">
											{totalDevices}
										</text>
										<text x="50%" y="58%" textAnchor="middle" dominantBaseline="middle" fill="#64748b" fontSize="12" fontWeight="500">
											Total Devices
										</text>
									</PieChart>
								</ResponsiveContainer>
							</div>

							<div className="grid gap-2.5 sm:grid-cols-2">
								{pieData.map((entry) => {
									const percent = totalDevices > 0 ? ((entry.value / totalDevices) * 100).toFixed(1) : "0.0";
									const isActive = selectedSlice === entry.key;
									return (
										<button
											key={entry.key}
											onClick={() => setSelectedSlice((prev) => (prev === entry.key ? null : entry.key))}
											className={`text-left rounded-xl border px-3 py-2.5 transition-all ${
												isActive
													? "border-slate-400 bg-slate-50 shadow-sm"
													: "border-slate-200 bg-white hover:border-slate-300"
											}`}
										>
											<div className="flex items-center justify-between gap-3">
												<div className="flex items-center gap-2 min-w-0">
													<span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
													<p className="text-xs font-semibold text-slate-700 truncate">{entry.label}</p>
												</div>
												<p className="text-[11px] text-slate-500">{percent}%</p>
											</div>
											<p className="text-lg font-bold text-slate-900 mt-1">{entry.value}</p>
										</button>
									);
								})}
							</div>
						</div>
					)}
				</div>

				<div className="flex flex-wrap gap-2 pt-1">
					<button
						onClick={() => setSelectedSlice((prev) => (prev === "updated" ? null : "updated"))}
						className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${
							selectedSlice === "updated"
								? "bg-green-100 border-green-300 text-green-700"
								: "bg-white border-slate-300 text-slate-600"
						}`}
					>
						Updated ({updatedCount})
					</button>
					<button
						onClick={() => setSelectedSlice((prev) => (prev === "pending" ? null : "pending"))}
						className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${
							selectedSlice === "pending"
								? "bg-amber-100 border-amber-300 text-amber-700"
								: "bg-white border-slate-300 text-slate-600"
						}`}
					>
						Pending ({pendingCount})
					</button>
					<button
						onClick={() =>
							setSelectedSlice((prev) => (prev === "mandatory_wait" ? null : "mandatory_wait"))
						}
						className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${
							selectedSlice === "mandatory_wait"
								? "bg-orange-100 border-orange-300 text-orange-700"
								: "bg-white border-slate-300 text-slate-600"
						}`}
					>
						Mandatory Wait ({mandatoryWaitCount})
					</button>
					<button
						onClick={() => setSelectedSlice((prev) => (prev === "failed" ? null : "failed"))}
						className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${
							selectedSlice === "failed"
								? "bg-red-100 border-red-300 text-red-700"
								: "bg-white border-slate-300 text-slate-600"
						}`}
					>
						Failed ({failedCount})
					</button>
				</div>

				<div>
					<h3 className="text-sm font-semibold text-slate-700 mb-3">
						{selectedSlice
							? `${getSliceLabel(selectedSlice)} Devices (${tableDevices.length})`
							: `All Devices (${tableDevices.length})`}
					</h3>
					{tableDevices.length === 0 ? (
						<p className="text-sm text-slate-400">No devices in this category.</p>
					) : (
						<div className="overflow-x-auto rounded-lg border border-slate-200">
							<table className="min-w-full text-xs">
								<thead className="bg-slate-50">
									<tr>
										{[
											"Serial Number",
											"Model",
											"Current Country",
											"Current FW",
											"Base FW",
											"Status",
											"Latest Updated At",
										].map((header) => (
											<th
												key={header}
												className="px-3 py-2 text-left font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap"
											>
												{header}
											</th>
										))}
									</tr>
								</thead>
								<tbody className="divide-y divide-slate-100">
									{tableDevices.map((device) => {
										const statusMeta = getStatusMeta(device, device.__rolloutCategory);
										return (
										<tr key={device.device_id} className="hover:bg-slate-50 transition-colors">
											<td className="px-3 py-2 text-slate-600 whitespace-nowrap">
												{device.serial_number ?? "—"}
											</td>
											<td className="px-3 py-2 text-slate-600 whitespace-nowrap">
												{device.model ?? "—"}
											</td>
											<td className="px-3 py-2 text-slate-600 whitespace-nowrap">
												{device.current_country ?? "—"}
											</td>
											<td className="px-3 py-2 text-slate-600 whitespace-nowrap">
												{device.current_fw_version ?? "—"}
											</td>
											<td className="px-3 py-2 text-slate-600 whitespace-nowrap">
												{device.base_firmware_version ?? "—"}
											</td>
											<td className="px-3 py-2 whitespace-nowrap">
												<span
													className={`inline-block px-2 py-0.5 rounded-full font-semibold capitalize ${statusMeta.className}`}
												>
													{statusMeta.label}
												</span>
											</td>
											<td className="px-3 py-2 text-slate-500 whitespace-nowrap">
											{device.latest_updated_at ? device.latest_updated_at.split('T').join(' ').substring(0, 19) : "—"}
											</td>
										</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
